// dialog-ref.ts
import { OverlayRef } from '@angular/cdk/overlay';
import { Observable, Subject } from 'rxjs';
import { DialogComponent } from './dialog-component';

export class DialogRef<R = unknown, D = unknown> {
  private readonly afterClosedSubject = new Subject<R | undefined>();
  private closed = false;
  private result: R | undefined;

  private _componentInstance: DialogComponent<D, R> | undefined;

  constructor(private overlayRef: OverlayRef) {}

  /**
   * Sets the component instance reference
   * @internal Used by DialogService
   */
  setComponentInstance(instance: DialogComponent<D, R>): void {
    this._componentInstance = instance;
  }

  /**
   * Gets the component instance
   */
  get componentInstance(): DialogComponent<D, R> | undefined {
    return this._componentInstance;
  }

  /**
   * Close the dialog
   * @param result Optional result to pass to the afterClosed observable
   */
  close(result?: R): void {
    if (!this.closed) {
      this.closed = true;
      this.overlayRef.dispose();
      this.afterClosedSubject.next(result !== undefined ? result : this.result);
      this.afterClosedSubject.complete();
    }
  }

  /**
   * Stores the result without closing the dialog
   * @param result The result to store
   */
  setResult(result: R): void {
    this.result = result;
  }

  /**
   * Check if the dialog is closed
   */
  isClosed(): boolean {
    return this.closed;
  }

  /**
   * Get the current stored result
   */
  getResult(): R | undefined {
    return this.result;
  }

  /**
   * Get an observable that emits when the dialog is closed
   */
  afterClosed(): Observable<R | undefined> {
    return this.afterClosedSubject.asObservable();
  }
}
